import { CerberusStatsFields } from '../cerberus-stats';
import { HcMapState } from '../maps';
import { PersonaData } from '../personas';
import { CompFields, PropertyStateFields } from '../property-state';
import { CompsListViewType, PreferencePDFExportType } from '../report-api';

export enum PersonaTypesOrg {
  CollateralInvestor = 'collateral-investor',
  ServiceProvider = 'service-provider',
  MortgageLender = 'mortgage-lender',
}

export enum PreferencesKeys {
  SubjectDataPriority = 'subjectDataPriority',
  CompTableColumns = 'tableColsComps',
  CompTableWidth = 'tableWidthComps',
  RentalCompTableColumns = 'tableColsRentalComps',
  PhotoGrid = 'photoGrid',
  Maps = 'maps',
  AckRefreshComps = 'skipWarningRefreshComps',
  AckRefreshRentalComps = 'skipWarningRefreshRentalComps',
  ReportFeaturesSubject = 'reportFeaturesSubject',
  ReportFeaturesComps = 'reportFeaturesComps',
  ReportFeaturesRentalComps = 'reportFeaturesRentalComps',
  // Old, will be removed soon
  Persona = 'persona',
  // New, use this
  UserPersona = 'userPersona',
  MsaFavorites = 'msaFavorites',
  GeoInterested = 'geoInterested',
  MarketStateColumnsMsa = 'marketStateColumnsMsa',
  MarketStateColumnsZip = 'marketStateColumnsZip',
  MarketStateChartsMsa = 'marketStateChartsMsa',
  MarketStateChartsZip = 'marketStateChartsZip',
  CompsMapSale = 'compsMapSale',
  CompsMapRental = 'compsMapRental',
  PexpRedesignEnabled = 'pexpRedesignEnabled',
  CompListViewTypeDefault = 'compsListViewTypeDefault',
  PDFExportTypeDefault = 'pdfExportContentTypeDefault',
}

export enum ReportFeaturesSubject {
  Comments = 'comments',
  Sharing = 'sharing',
  Download = 'download',
  KeyFacts = 'keyFacts',
  Insights = 'insights',
  Personalization = 'personalization',
  Refresh = 'refresh',
  HeatMaps = 'heatmaps',
}

export enum ReportFeaturesComps {
  Enabled = 'enabled',
  RefreshComps = 'refreshReport',
  AddCompByMlsNumber = 'addCompByMlsNumber',
  SavedCompFilters = 'savedCompFilters',
  Tags = 'tags',
  AdjustComps = 'adjustComps',
  CustomAreaCompSearch = 'customAreaCompSearch',
  AutoSelectHcSuggestedComps = 'autoSelectHcSuggestedComps',
  KeywordSearch = 'keywordSearch',
}

export enum ReportFeaturesRentalComps {
  Enabled = 'enabled',
  RefreshComps = 'refreshReport',
  AddCompByMlsNumber = 'addCompByMlsNumber',
  SavedCompFilters = 'savedCompFilters',
  Tags = 'tags',
  AdjustComps = 'adjustComps',
  CustomAreaCompSearch = 'customAreaCompSearch',
  AutoSelectHcSuggestedComps = 'autoSelectHcSuggestedComps',
  KeywordSearch = 'keywordSearch',
}

export interface MapPreferences {
  disableClusters: boolean;
}

interface PhotoGridPreferences {
  size: number;
}

type InactiveColumns<T extends string> = {
  [key in T]?: boolean;
};

export interface DataPriority<T extends string> {
  inactive: InactiveColumns<T>;
  order: T[];
}

type DataPrioritySubject = DataPriority<PropertyStateFields>;
export type DataPriorityComp = DataPriority<PropertyStateFields | CompFields>;
export type DataPriorityCerberusStats = DataPriority<CerberusStatsFields>;

export type GeoInterestedType = 'msas' | 'zips' | 'cities';
export interface GeoInterested {
  ['msas']?: string[];
  ['zips']?: string[];
  ['cities']?: string[];
}

export interface HcMapPreferences {
  heatmap: Omit<HcMapState['heatmap'], 'propertyType'>;
}

export interface UiPreferences {
  [PreferencesKeys.SubjectDataPriority]?: DataPrioritySubject;
  [PreferencesKeys.CompTableColumns]?: DataPriorityComp;
  [PreferencesKeys.CompTableWidth]?: number;
  [PreferencesKeys.RentalCompTableColumns]?: DataPriorityComp;
  [PreferencesKeys.PhotoGrid]?: PhotoGridPreferences;
  [PreferencesKeys.Maps]?: MapPreferences;
  [PreferencesKeys.AckRefreshComps]?: boolean;
  [PreferencesKeys.AckRefreshRentalComps]?: boolean;
  [PreferencesKeys.Persona]?: PersonaTypesOrg;
  [PreferencesKeys.ReportFeaturesSubject]?: {
    [key in ReportFeaturesSubject]?: boolean;
  };
  [PreferencesKeys.ReportFeaturesComps]?: {
    [key in ReportFeaturesComps]?: boolean;
  };
  [PreferencesKeys.ReportFeaturesRentalComps]?: {
    [key in ReportFeaturesRentalComps]?: boolean;
  };
  [PreferencesKeys.MsaFavorites]?: string[];
  [PreferencesKeys.GeoInterested]?: GeoInterested;
  [PreferencesKeys.MarketStateChartsMsa]?: DataPriorityCerberusStats;
  [PreferencesKeys.MarketStateChartsZip]?: DataPriorityCerberusStats;
  [PreferencesKeys.MarketStateColumnsMsa]?: DataPriorityCerberusStats;
  [PreferencesKeys.MarketStateColumnsZip]?: DataPriorityCerberusStats;
  [PreferencesKeys.CompsMapSale]?: HcMapPreferences;
  [PreferencesKeys.CompsMapRental]?: HcMapPreferences;
  [PreferencesKeys.PexpRedesignEnabled]?: boolean;
  [PreferencesKeys.CompListViewTypeDefault]?: CompsListViewType;
  [PreferencesKeys.PDFExportTypeDefault]?: PreferencePDFExportType;
  [PreferencesKeys.UserPersona]?: PersonaData;
}
